// src/taskpane/features/integrations/components/dartsip/DartsIPPanel.tsx

import React, { useState } from 'react';
import { ArrowLeft, TrendingUp, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DartsIPSearchParams, DartsIPResponse, JurisprudenceCase } from '../../types/integrationTypes';
import { dartsipService } from '../../services/dartsipService';
import SearchFilters from './SearchFilters';
import JurisprudenceList from './JurisprudenceList';
import { documentCacheService } from '../../../../shared/services/documentCacheService';
import { getFoldersForKnowledgeBase, uploadDocumentToKnowledgeBase, createFolder } from '../../../knowledgebase/services/documentUploadService';
import { DocumentViewer } from '../../../knowledgebase/components/molecules';
import { KnowledgeBaseDocument } from '../../../../types/navigationTypes';

interface DartsIPPanelProps {
  executionId: string;
  workpillId: string;
  onBack: () => void;
}

const DartsIPPanel: React.FC<DartsIPPanelProps> = ({ 
  executionId,
  workpillId,
  onBack 
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<DartsIPResponse | null>(null);
  const [cases, setCases] = useState<JurisprudenceCase[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [addingCaseId, setAddingCaseId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [viewingDocument, setViewingDocument] = useState<KnowledgeBaseDocument | null>(null);

  const handleSearch = async (params: DartsIPSearchParams) => {
    setIsSearching(true);
    setError(null);
    try {
      const response = await dartsipService.searchJurisprudence(params);
      setSearchResults(response);
      setCases(response.results);
    } catch (err) {
      setError('Error searching jurisprudence. Please try again.');
      console.error('Search error:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleViewDocument = (case_id: string) => {
    const selectedCase = cases.find(c => c.case_id === case_id);
    if (!selectedCase) return;

    // Buscar el primer documento con URL válida
    const docWithUrl = selectedCase.documents.find(
      doc => doc.url && !doc.url.includes('PLACEHOLDER')
    );

    if (!docWithUrl || !docWithUrl.url) {
      setError('No documents available to view');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Convertir el documento de jurisprudencia a KnowledgeBaseDocument
    const kbDocument: KnowledgeBaseDocument = {
      id: selectedCase.case_id,
      name: selectedCase.title,
      description: selectedCase.ai_summary?.short,
      created_at: selectedCase.decision_date,
      status: 'completed',
      knowledge_base_id: '',
      knowledge_base_name: 'DartsIP',
      url: docWithUrl.url,
      type: 'file'
    };

    setViewingDocument(kbDocument);
  };

  const handleCloseViewer = () => {
    setViewingDocument(null);
  };

  const handleAddToKnowledgeBase = async (case_id: string) => {
    setAddingCaseId(case_id);
    setError(null);
    setSuccessMessage(null);

    try {
      const selectedCase = cases.find(c => c.case_id === case_id);
      if (!selectedCase) {
        throw new Error('Case not found');
      }

      // 1. Obtener o crear la KB de la ejecución
      const kbList = await documentCacheService.getDocuments(executionId, workpillId);
      let targetKB = kbList.find(kb => kb.is_global);
      
      if (!targetKB) {
        throw new Error('Knowledge base for execution not found');
      }

      // 2. Crear o obtener la carpeta "DartsIP" en la KB
      const folders = await getFoldersForKnowledgeBase(targetKB.id);
      let dartsipFolder = folders.find(f => f.name === 'DartsIP');
      
      if (!dartsipFolder) {
        dartsipFolder = await createFolder(targetKB.id, 'DartsIP', 'DartsIP Jurisprudence');
      }

      // 3. Buscar el PDF del caso
      const pdfDoc = selectedCase.documents.find(doc => 
        doc.format === 'pdf' && doc.url && !doc.url.includes('PLACEHOLDER')
      );

      if (!pdfDoc || !pdfDoc.url) {
        throw new Error('No PDF available for this case');
      }

      // 4. Descargar el PDF
      const response = await fetch(pdfDoc.url);
      if (!response.ok) {
        throw new Error(`Error downloading PDF: ${response.statusText}`);
      }
      
      const pdfBlob = await response.blob();

      // 5. Crear el archivo PDF
      const file = new File(
        [pdfBlob], 
        `${selectedCase.case_id}.pdf`, 
        { type: 'application/pdf' }
      );

      // 6. Preparar metadata para la descripción
      const metadata = `${selectedCase.ai_summary?.short || ''}

Parties: ${selectedCase.parties.applicant.join(', ')} vs ${selectedCase.parties.opponent.join(', ')}
Outcome: ${selectedCase.outcome.result} - Winner: ${selectedCase.outcome.winner}
Court: ${selectedCase.court} (${selectedCase.jurisdiction})
Date: ${new Date(selectedCase.decision_date).toLocaleDateString('en-US')}`;

      // 7. Subir el PDF a la KB
      await uploadDocumentToKnowledgeBase({
        knowledgeBaseId: targetKB.id,
        file: file,
        documentName: selectedCase.title,
        description: metadata,
        folderId: dartsipFolder.id
      });

      setSuccessMessage(`Case "${selectedCase.title}" correctly added to the knowledge base in the DartsIP folder`);
      
      // Limpiar el mensaje después de 5 segundos
      setTimeout(() => setSuccessMessage(null), 5000);

    } catch (err) {
      console.error('Error adding to KB:', err);
      setError('Error adding case to the knowledge base. Please try again.');
    } finally {
      setAddingCaseId(null);
    }
  };

  // Si hay un documento siendo visualizado, mostrar el visor
  if (viewingDocument) {
    return <DocumentViewer document={viewingDocument} onClose={handleCloseViewer} />;
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Content with Scroll - Header dentro del scroll */}
      <div className="flex-1 overflow-y-auto">
        {/* Header */}
        <div className="px-4 pt-4 pb-3 flex items-center gap-3 mb-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="w-8 h-8"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <img 
            src="assets/logos/clarivate-logo.png" 
            alt="Clarivate Darts-IP logo"
            className="h-7 object-contain"
          />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-foreground">Darts IP</h2>
            <p className="text-xs text-muted-foreground">Intellectual property jurisprudence search</p>
          </div>
        </div>
        {/* Search Filters */}
        <SearchFilters onSearch={handleSearch} isSearching={isSearching} />
        
        {/* Success Message */}
        {successMessage && (
          <div className="mx-4 mb-3 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-start gap-2">
              <TrendingUp className="w-4 h-4 text-green-600 mt-0.5" />
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          </div>
        )}
        
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mx-4 mb-3">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {/* Search Results */}
        <JurisprudenceList 
          cases={cases}
          isLoading={isSearching}
          onViewDocument={handleViewDocument}
          onAddToKnowledgeBase={handleAddToKnowledgeBase}
          addingCaseId={addingCaseId}
        />
      </div>
    </div>
  );
};

export default DartsIPPanel;
